/**
 * Shared start-config for a new recording: languages + live/batch mode.
 * Used by the Full dialog, Lite instant-start, and RecordingControls when
 * sessionStorage `meeting-config:<id>` is missing (new tab, refresh).
 */

export type TMeetingStartMode = "live" | "batch"

export interface IMeetingStartConfig {
  languages: string[]
  autoDetect: boolean
  mode: TMeetingStartMode
  /**
   * Start recording as soon as the meeting screen is ready.
   * Set only when the user already pressed start and the microphone was granted.
   */
  autoStart: boolean
}

export interface IStoredMeetingStartConfig {
  languages?: string[] | null
  langs?: string[] | null
  autoDetect?: boolean
  /** Dialog writes `live`; the recorder uses `streaming`. Both mean live. */
  mode?: TMeetingStartMode | "streaming" | null
  /** Explicit true only. Missing, false, or a non-boolean never auto-starts. */
  autoStart?: boolean
}

/**
 * Seed languages + autoDetect from the company's `default_language` setting.
 * `"multi"` → auto-detect with no language hints; comma-separated codes otherwise.
 */
export function languagesFromDefaultSetting(
  raw: string | undefined,
): Pick<IMeetingStartConfig, "languages" | "autoDetect"> {
  if (raw === "multi") {
    return { languages: [], autoDetect: true }
  }
  const languages = (raw ?? "")
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean)
  if (languages.length === 0) {
    return { languages: [], autoDetect: true }
  }
  return { languages, autoDetect: false }
}

/**
 * Resolve the recorder's initial languages/mode.
 *
 * Missing sessionStorage used to hardcode `["nl"]` + `batch`, which both
 * ignored the company default and dropped Lite into the empty batch screen.
 * Default is now live + the company language list (or auto-detect).
 */
/** sessionStorage `live` is the dialog name for RecordingControls `streaming`. */
export function toRecordingMode(mode: TMeetingStartMode | "streaming" | null | undefined): "batch" | "streaming" {
  return mode === "batch" ? "batch" : "streaming"
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

/**
 * Read the auto-start flag from a `meeting-config:<id>` sessionStorage value.
 * Only the boolean `true` counts. Malformed JSON, a missing key, or `"true"`
 * stay off so a refresh never starts a recording the user did not ask for.
 */
export function readAutoStartFlag(raw: string | null): boolean {
  if (!raw) return false
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return false
  }
  if (!isRecord(parsed)) return false
  return parsed.autoStart === true
}

/**
 * Remove `autoStart` from a `meeting-config:<id>` value after it has fired.
 * `null` or empty input stays `null`, so the caller does not create a key.
 * Malformed JSON and payloads without the flag are returned unchanged.
 * Every other field is kept.
 */
export function clearAutoStartFlag(raw: string | null): string | null {
  if (raw == null || raw === "") return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return raw
  }
  if (!isRecord(parsed) || !("autoStart" in parsed)) return raw
  const next = { ...parsed }
  delete next.autoStart
  return JSON.stringify(next)
}

export function resolveRecordingStart(args: {
  stored: IStoredMeetingStartConfig | null
  companyDefaultLanguage: string | undefined
}): IMeetingStartConfig {
  const fromCompany = languagesFromDefaultSetting(args.companyDefaultLanguage)
  const stored = args.stored
  if (!stored) {
    return { ...fromCompany, mode: "live", autoStart: false }
  }

  const autoDetect = stored.autoDetect === true
  const storedLangs = (stored.languages ?? stored.langs ?? []).filter(
    (item): item is string => typeof item === "string" && item.length > 0,
  )
  const mode: TMeetingStartMode = stored.mode === "batch" ? "batch" : "live"
  const autoStart = stored.autoStart === true

  if (autoDetect) {
    return { languages: [], autoDetect: true, mode, autoStart }
  }
  if (storedLangs.length > 0) {
    return { languages: storedLangs, autoDetect: false, mode, autoStart }
  }
  return { ...fromCompany, mode, autoStart }
}
